<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TmkDateRangeField from './TmkDateRangeField.vue';

const props = defineProps({
  modelValue: { type: Object, required: true },
  labels: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  currentUserId: { type: [String, Number], default: '' },
  assignedOnly: { type: Boolean, default: false },
  showSearch: { type: Boolean, default: true },
  includeUnclassified: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const dateRef = ref(null);
const labelSearch = ref('');
const groups = ['labelsAll', 'labelsAny', 'labelsNone'];
const labelGroup = ref(
  groups.find(key => props.modelValue[key]?.length) || 'labelsAll'
);
const update = (key, value) =>
  emit('update:modelValue', { ...props.modelValue, [key]: value });
const dates = computed({
  get: () => ({
    from: props.modelValue.createdFrom,
    to: props.modelValue.createdTo,
  }),
  set: value =>
    emit('update:modelValue', {
      ...props.modelValue,
      createdFrom: value.from,
      createdTo: value.to,
    }),
});
const selectedLabels = computed(() =>
  groups.flatMap(group =>
    (props.modelValue[group] || []).map(label => ({ group, label }))
  )
);
const filteredLabels = computed(() =>
  props.labels.filter(label =>
    label.title
      .toLocaleLowerCase()
      .includes(labelSearch.value.trim().toLocaleLowerCase())
  )
);
const toggleLabel = label => {
  const values = props.modelValue[labelGroup.value] || [];
  update(
    labelGroup.value,
    values.includes(label)
      ? values.filter(value => value !== label)
      : [...values, label]
  );
};
const changeLabelMode = event => {
  const nextGroup = event.target.value;
  const previousGroup = labelGroup.value;
  labelGroup.value = nextGroup;
  emit('update:modelValue', {
    ...props.modelValue,
    [previousGroup]: [],
    [nextGroup]: [
      ...new Set([
        ...(props.modelValue[previousGroup] || []),
        ...(props.modelValue[nextGroup] || []),
      ]),
    ],
  });
};
const removeLabel = ({ group, label }) =>
  update(
    group,
    props.modelValue[group].filter(value => value !== label)
  );
const validate = () => dateRef.value?.validate() ?? true;
defineExpose({ validate });
</script>

<template>
  <div class="flex flex-col gap-5">
    <label
      v-if="showSearch"
      class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
    >
      {{ t('CHAT_LIST.TMK.SEARCH') }}
      <input
        type="search"
        data-testid="tmk-search"
        :value="modelValue.q"
        :placeholder="t('CHAT_LIST.TMK.SEARCH')"
        class="!m-0 !h-9 !rounded-lg !border-n-weak !text-sm"
        @input="update('q', $event.target.value)"
      />
    </label>
    <TmkDateRangeField
      ref="dateRef"
      v-model="dates"
      :label="t('CHAT_LIST.TMK.CREATED_DATE')"
    />
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label
        v-if="!assignedOnly"
        class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CHAT_LIST.TMK.ASSIGNED_AGENT') }}
        <select
          :value="modelValue.assigneeId"
          data-testid="tmk-agent"
          class="!m-0 !h-9 !rounded-lg !border-n-weak !text-sm"
          @change="update('assigneeId', $event.target.value)"
        >
          <option value="">{{ t('CHAT_LIST.TMK.ALL_AGENTS') }}</option>
          <option v-if="currentUserId" :value="String(currentUserId)">
            {{ t('CHAT_LIST.TMK.ME') }}
          </option>
          <option value="unassigned">
            {{ t('CHAT_LIST.TMK.UNASSIGNED') }}
          </option>
          <option
            v-for="agent in agents"
            :key="agent.id"
            :value="String(agent.id)"
          >
            {{ agent.name }}
          </option>
        </select>
      </label>
      <label
        v-if="!assignedOnly"
        class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CHAT_LIST.TMK.TEAM') }}
        <select
          :value="modelValue.assignedTeamId"
          data-testid="tmk-team"
          class="!m-0 !h-9 !rounded-lg !border-n-weak !text-sm"
          @change="update('assignedTeamId', $event.target.value)"
        >
          <option value="">{{ t('CHAT_LIST.TMK.ALL_TEAMS') }}</option>
          <option v-for="team in teams" :key="team.id" :value="String(team.id)">
            {{ team.name }}
          </option>
        </select>
      </label>
      <label
        class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12 sm:col-span-2"
      >
        {{ t('CHAT_LIST.TMK.ATTENTION') }}
        <select
          :value="modelValue.attention"
          data-testid="tmk-attention"
          class="!m-0 !h-9 !rounded-lg !border-n-weak !text-sm"
          @change="update('attention', $event.target.value)"
        >
          <option value="all">{{ t('CHAT_LIST.TMK.ATTENTION_ALL') }}</option>
          <option value="unattended">
            {{ t('CHAT_LIST.TMK.UNATTENDED') }}
          </option>
          <option value="following_up">
            {{ t('CHAT_LIST.TMK.FOLLOWING_UP') }}
          </option>
          <option v-if="includeUnclassified" value="unclassified">
            {{ t('CHAT_LIST.TMK.UNCLASSIFIED') }}
          </option>
        </select>
      </label>
    </div>
    <fieldset class="min-w-0 flex flex-col gap-2">
      <legend class="mb-2 text-sm font-medium text-n-slate-12">
        {{ t('CHAT_LIST.TMK.LABELS') }}
      </legend>
      <div class="flex gap-2">
        <select
          :value="labelGroup"
          :aria-label="t('CHAT_LIST.TMK.LABEL_MATCH')"
          data-testid="tmk-label-mode"
          class="!m-0 !h-9 !w-auto !rounded-lg !border-n-weak !text-sm"
          @change="changeLabelMode"
        >
          <option v-for="group in groups" :key="group" :value="group">
            {{ t(`CHAT_LIST.TMK.LABEL_MODES.${group}`) }}
          </option>
        </select>
        <input
          v-model="labelSearch"
          type="search"
          :aria-label="t('CHAT_LIST.TMK.FIND_LABEL')"
          :placeholder="t('CHAT_LIST.TMK.FIND_LABEL')"
          class="!m-0 !h-9 !min-w-0 !rounded-lg !border-n-weak !text-sm"
        />
      </div>
      <p class="!m-0 text-xs text-n-slate-10">
        {{ t(`CHAT_LIST.TMK.LABEL_HELP.${labelGroup}`) }}
      </p>
      <div
        class="grid max-h-32 grid-cols-1 gap-1 overflow-y-auto rounded-lg border border-n-weak p-2 sm:grid-cols-2"
      >
        <label
          v-for="label in filteredLabels"
          :key="label.id"
          class="flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-slate-3"
        >
          <input
            type="checkbox"
            :checked="modelValue[labelGroup]?.includes(label.title)"
            class="!m-0 shrink-0"
            @change="toggleLabel(label.title)"
          />
          <span class="truncate" :title="label.title">{{ label.title }}</span>
        </label>
        <span
          v-if="!filteredLabels.length"
          class="p-2 text-xs text-n-slate-10"
          >{{ t('CHAT_LIST.TMK.NO_LABELS_FOUND') }}</span
        >
      </div>
      <div v-if="selectedLabels.length" class="flex flex-wrap gap-1.5">
        <button
          v-for="item in selectedLabels"
          :key="`${item.group}/${item.label}`"
          type="button"
          :aria-label="t('CHAT_LIST.TMK.REMOVE_FILTER', { filter: item.label })"
          class="flex max-w-full items-center gap-1 rounded-md bg-n-teal-3 px-2 py-1 text-xs text-n-teal-11"
          @click="removeLabel(item)"
        >
          <span class="truncate"
            >{{ t(`CHAT_LIST.TMK.LABEL_MODES.${item.group}`) }}:
            {{ item.label }}</span
          >
          <span class="i-lucide-x size-3 shrink-0" aria-hidden="true" />
        </button>
      </div>
    </fieldset>
  </div>
</template>
